import { addDays, addWeeks, format } from "date-fns";
import type { FieldValues, Path, UseFormSetError } from "react-hook-form";
import { hasApiError } from "utils/error-utils";

import type { TaskFormData } from "../types/taskModal.types";

const TASK_PAYLOAD_TIME_SUFFIX = " 12:00 AM";

type FieldErrorNotifier = (message: string) => void;

type ApplyApiFieldErrorsParams<TFormValues extends FieldValues> = {
  error: unknown;
  setError: UseFormSetError<TFormValues>;
  onFieldError?: FieldErrorNotifier;
};

/* build default task modal values with a one-week due date. */
export const createTaskModalDefaultValues = (): TaskFormData => ({
  message: "",
  assignee: null,
  due_date: format(addWeeks(new Date(), 1), "MM/dd/yyyy"),
  file_id: 0,
  type: "",
  service: null,
});

/* build default send-to-ops values with a next-day due date. */
export const createSendToOpsDefaultValues = (): TaskFormData => ({
  message: "",
  assignee: null,
  due_date: format(addDays(new Date(), 1), "MM/dd/yyyy"),
  file_id: 0,
  type: "",
  service: null,
});

/* normalize react-select style values into numeric ids for payloads. */
export const toNumericSelectValue = (
  value: TaskFormData["assignee"] | TaskFormData["service"],
): number | null => {
  if (typeof value === "number") {
    return value;
  }

  if (value && typeof value === "object") {
    const optionValue = value.value;
    if (typeof optionValue === "number") {
      return optionValue;
    }
  }

  return null;
};

/* map unknown select values to the expected string task type code. */
export const toSingleSelectStringValue = (option: unknown): string => {
  if (!option || Array.isArray(option) || typeof option !== "object") {
    return "";
  }

  if (!("value" in option)) {
    return "";
  }

  return typeof option.value === "string" ? option.value : "";
};

/* convert due date to backend format while preserving empty values. */
export const toTaskDueDatePayload = (
  dueDate: string | null | undefined,
): string | null => {
  if (!dueDate) {
    return null;
  }

  return dueDate + TASK_PAYLOAD_TIME_SUFFIX;
};

/* apply api validation errors onto form fields and optional toast callback. */
export const applyApiFieldErrors = <TFormValues extends FieldValues>({
  error,
  setError,
  onFieldError,
}: ApplyApiFieldErrorsParams<TFormValues>): boolean => {
  if (!hasApiError(error)) {
    return false;
  }

  Object.entries(error.data).forEach(([field, messages]) => {
    const message = Array.isArray(messages) ? messages[0] : messages;

    setError(field as Path<TFormValues>, {
      type: "manual",
      message,
    });

    if (onFieldError) {
      onFieldError(String(message));
    }
  });

  return true;
};
